type DialogKind = 'alert' | 'confirm' | 'prompt';

export interface AppDialogOptions {
  title?: string;
  confirmText?: string;
  cancelText?: string;
  defaultValue?: string;
}

let activeDialog: HTMLElement | null = null;

function showDialog(kind: DialogKind, message: unknown, options: AppDialogOptions = {}): Promise<boolean | string | null> {
  if (activeDialog) activeDialog.remove();

  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.className = 'app-dialog-overlay';
    overlay.setAttribute('role', 'presentation');

    const dialog = document.createElement('section');
    dialog.className = 'app-dialog';
    dialog.setAttribute('role', kind === 'alert' ? 'alertdialog' : 'dialog');
    dialog.setAttribute('aria-modal', 'true');

    const heading = document.createElement('h2');
    heading.textContent = options.title || (kind === 'alert' ? 'Notice' : kind === 'prompt' ? 'Enter information' : 'Please confirm');
    const body = document.createElement('p');
    body.textContent = String(message ?? '');
    dialog.append(heading, body);

    let input: HTMLInputElement | null = null;
    if (kind === 'prompt') {
      input = document.createElement('input');
      input.className = 'app-dialog-input';
      input.value = options.defaultValue || '';
      input.setAttribute('aria-label', heading.textContent);
      dialog.append(input);
    }

    const actions = document.createElement('div');
    actions.className = 'app-dialog-actions';

    const finish = (value: boolean | string | null) => {
      document.removeEventListener('keydown', onKeyDown);
      overlay.remove();
      if (activeDialog === overlay) activeDialog = null;
      resolve(value);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') finish(kind === 'alert' ? true : null);
      if (event.key === 'Enter' && (kind !== 'prompt' || event.target === input)) {
        finish(kind === 'prompt' ? input?.value ?? '' : true);
      }
    };

    if (kind !== 'alert') {
      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.className = 'app-dialog-button app-dialog-button--secondary';
      cancel.textContent = options.cancelText || 'Cancel';
      cancel.addEventListener('click', () => finish(null));
      actions.append(cancel);
    }

    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'app-dialog-button app-dialog-button--primary';
    confirm.textContent = options.confirmText || (kind === 'alert' ? 'OK' : 'Confirm');
    confirm.addEventListener('click', () => finish(kind === 'prompt' ? input?.value ?? '' : true));
    actions.append(confirm);
    dialog.append(actions);
    overlay.append(dialog);
    document.body.append(overlay);
    activeDialog = overlay;
    document.addEventListener('keydown', onKeyDown);
    requestAnimationFrame(() => (input || confirm).focus());
  });
}

export const appAlert = (message: unknown, options?: AppDialogOptions): Promise<void> =>
  showDialog('alert', message, options).then(() => undefined);

export const appConfirm = (message: unknown, options?: AppDialogOptions): Promise<boolean> =>
  showDialog('confirm', message, options).then(Boolean);

export const appPrompt = (message: unknown, defaultValue = '', options?: AppDialogOptions): Promise<string | null> =>
  showDialog('prompt', message, { ...options, defaultValue }).then(value => typeof value === 'string' ? value : null);

declare global {
  interface Window {
    appAlert: typeof appAlert;
    appConfirm: typeof appConfirm;
    appPrompt: typeof appPrompt;
  }
}

window.appAlert = appAlert;
window.appConfirm = appConfirm;
window.appPrompt = appPrompt;

